import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import type {
  CreateTicketPayload,
  TicketPriority,
} from "../../Models/helpDesk";
import {
  useCreateTicket,
  useUploadTicketAttachments,
} from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import AttachmentInput, { useAttachmentFiles } from "./AttachmentInput";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: `${color.white} !important`,
    borderRadius: "8px",
    color: `${color.primary_dark} !important`,
    fontSize: "12px",
    "&:not(.MuiInputBase-multiline)": {
      height: "38px",
    },
  },
  "& .MuiInputBase-input, & textarea": {
    color: `${color.primary_dark} !important`,
    WebkitTextFillColor: `${color.primary_dark} !important`,
    fontSize: "12px",
  },
  "& .MuiInputLabel-root": {
    color: `${color.gray500} !important`,
    fontSize: "12px",
    transform: "translate(14px, 11px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "&:has(.MuiInputBase-multiline) .MuiInputLabel-root:not(.MuiInputLabel-shrink)":
    {
      transform: "translate(14px, 16px) scale(1)",
    },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray200} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.accent} !important`,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "10px",
    mt: 0.5,
  },
  "& .MuiSelect-select": {
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    color: color.primary_dark,
    fontSize: "18px",
  },
};

const CreateTicketModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation("helpDesk");
  const mutation = useCreateTicket();
  const uploadMutation = useUploadTicketAttachments();
  const attachments = useAttachmentFiles();
  const pending = mutation.isPending || uploadMutation.isPending;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [priority, setPriority] = useState<TicketPriority>(1);

  const reset = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setTags("");
    setPriority(1);
    attachments.clear();
  };

  const close = () => {
    if (pending) return;
    reset();
    onClose();
  };

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!title.trim() || pending) return;

    const payload: CreateTicketPayload = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      tags: tags
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
      priority,
    };

    const files = attachments.files;
    const finish = () => {
      reset();
      onClose();
      onCreated?.();
    };
    mutation.mutate(payload, {
      onSuccess: (ticket) => {
        if (!files.length) {
          finish();
          return;
        }
        // The ticket exists even if the upload fails; the upload error shows
        // as a snack and the files can be sent again in the conversation.
        uploadMutation.mutate({ id: ticket.id, files }, { onSettled: finish });
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(850px, 96vw)",
          maxWidth: "none",
          maxHeight: "92dvh",
          borderRadius: "18px",
          bgcolor: color.white,
          backgroundImage: "none",
          overflow: "hidden",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${color.gray200}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: color.blue50,
                display: "grid",
                placeItems: "center",
              }}
            >
              <AddIcon sx={{ color: color.secondary }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  color: `${color.primary_dark} !important`,
                  fontSize: 21,
                  fontWeight: 800,
                }}
              >
                {t("createDialog.title")}
              </Typography>

              <Typography
                sx={{
                  color: `${color.gray500} !important`,
                  fontSize: 12,
                }}
              >
                {t("createDialog.modalSubtitle")}
              </Typography>
            </Box>
          </Stack>

          <IconButton onClick={close} disabled={pending}>
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: { xs: 2, md: 3 },
            overflowY: "auto",
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <DescriptionOutlinedIcon sx={{ color: color.secondary }} />

                <Typography
                  sx={{
                    color: `${color.primary_dark} !important`,
                    fontWeight: 800,
                  }}
                >
                  {t("createDialog.detailsSection")}
                </Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  required
                  fullWidth
                  autoFocus
                  label={t("createDialog.ticketTitle")}
                  placeholder={t("createDialog.titlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  sx={fieldSx}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label={t("createDialog.description")}
                  placeholder={t("createDialog.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={fieldSx}
                />
              </Stack>
            </Box>

            <Divider sx={{ borderColor: color.gray200 }} />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <LocalOfferOutlinedIcon sx={{ color: color.secondary }} />

                <Typography
                  sx={{
                    color: `${color.primary_dark} !important`,
                    fontWeight: 800,
                  }}
                >
                  {t("createDialog.propertiesSection")}
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("createDialog.category")}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  sx={{
                    ...fieldSx,
                    "& .MuiSelect-select": {
                      color: `${color.primary_dark} !important`,
                      WebkitTextFillColor: `${color.primary_dark} !important`,
                    },
                    "& .MuiSelect-icon": {
                      color: color.primary_dark,
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: color.white,
                          backgroundImage: "none",
                          "& .MuiMenuItem-root": {
                            color: `${color.primary_dark} !important`,
                            fontSize: 14,
                            "&:hover": {
                              bgcolor: color.gray100,
                            },
                            "&.Mui-selected": {
                              bgcolor: color.blue50,
                            },
                            "&.Mui-selected:hover": {
                              bgcolor: color.blue50,
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value={t("categories.managedWorkspace")}>
                    {t("categories.managedWorkspace")}
                  </MenuItem>
                  <MenuItem value={t("categories.customWorkspace")}>
                    {t("categories.customWorkspace")}
                  </MenuItem>
                  <MenuItem value={t("categories.ambassadorWorkspace")}>
                    {t("categories.ambassadorWorkspace")}
                  </MenuItem>
                  <MenuItem value={t("categories.development")}>
                    {t("categories.development")}
                  </MenuItem>
                  <MenuItem value={t("categories.itSecurity")}>
                    {t("categories.itSecurity")}
                  </MenuItem>
                  <MenuItem value={t("categories.consulting")}>
                    {t("categories.consulting")}
                  </MenuItem>

                  <MenuItem value={t("categories.bugReport")}>
                    {t("categories.bugReport")}
                  </MenuItem>
                  <MenuItem value={t("categories.newRequirement")}>
                    {t("categories.newRequirement")}
                  </MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("createDialog.priority")}
                  value={priority}
                  onChange={(e) =>
                    setPriority(Number(e.target.value) as TicketPriority)
                  }
                  sx={{
                    ...fieldSx,
                    "& .MuiSelect-select": {
                      color: `${color.primary_dark} !important`,
                      WebkitTextFillColor: `${color.primary_dark} !important`,
                    },
                    "& .MuiSelect-icon": {
                      color: color.primary_dark,
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: color.white,
                          backgroundImage: "none",
                          "& .MuiMenuItem-root": {
                            color: `${color.primary_dark} !important`,
                            fontSize: 14,
                            "&:hover": {
                              bgcolor: color.gray100,
                            },
                            "&.Mui-selected": {
                              bgcolor: color.blue50,
                            },
                            "&.Mui-selected:hover": {
                              bgcolor: color.blue50,
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value={0}>{t("priority.low")}</MenuItem>
                  <MenuItem value={1}>{t("priority.normal")}</MenuItem>
                  <MenuItem value={2}>{t("priority.high")}</MenuItem>
                </TextField>

                <Box
                  sx={{
                    gridColumn: { sm: "1 / -1" },
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label={t("createDialog.tags")}
                    helperText={t("createDialog.tagsHelper")}
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    sx={fieldSx}
                  />
                </Box>

                <Box
                  sx={{
                    gridColumn: { sm: "1 / -1" },
                  }}
                >
                  <AttachmentInput
                    files={attachments.files}
                    error={attachments.error}
                    onAdd={attachments.add}
                    onRemove={attachments.remove}
                    disabled={pending}
                    showHint
                  />
                </Box>
              </Box>
            </Box>

            <Alert severity="info">{t("createDialog.info")}</Alert>

            {mutation.isError && (
              <Alert severity="error">{t("createDialog.error")}</Alert>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${color.gray200}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={close}
            disabled={pending}
            sx={{
              color: `${color.primary_dark} !important`,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {t("actions.cancel")}
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={pending || !title.trim()}
            startIcon={pending ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{
              bgcolor: `${color.accent} !important`,
              color: `${color.white} !important`,
              textTransform: "none",
              borderRadius: "9px",
              fontWeight: 700,
              px: 3,
              "&:hover": {
                bgcolor: `${color.accent_dark} !important`,
              },
            }}
          >
            {pending ? t("createDialog.submitting") : t("createDialog.submit")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateTicketModal;
